import { useSyncExternalStore } from 'react'

/** Bounds of the space after the last candle, px. */
export const MIN_RIGHT_GAP_PX = 16
export const MAX_RIGHT_GAP_PX = 400

export const clampGap = (px: number) => Math.round(Math.min(MAX_RIGHT_GAP_PX, Math.max(MIN_RIGHT_GAP_PX, px)))

export interface ChartGapStore {
  get(): number | null
  set(px: number): void
  subscribe(fn: () => void): () => void
}

/** A chart's gap after the last candle, remembered in the browser. */
export function createChartGapStore(storageKey: string, defaultPx: number | null = null): ChartGapStore {
  let value: number | null = defaultPx
  try {
    const saved = Number(localStorage.getItem(storageKey))
    if (Number.isFinite(saved) && saved > 0) value = clampGap(saved)
  } catch {
    // Storage blocked: the default holds.
  }
  const listeners = new Set<() => void>()
  return {
    get: () => value,
    set(px) {
      const next = clampGap(px)
      if (next === value) return
      value = next
      try {
        localStorage.setItem(storageKey, String(next))
      } catch {
        // Storage blocked: kept for this visit only.
      }
      listeners.forEach((fn) => fn())
    },
    subscribe(fn) {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
  }
}

export function useChartGap(store: ChartGapStore): number | null {
  return useSyncExternalStore(store.subscribe, store.get, store.get)
}

/** The Create chart's gap, shared by every state on it (the walkthrough too). */
export const mainChartGap = createChartGapStore('ab-chart-right-gap', 72)

/** The Backtest tab chart's own gap; none until the user drags it. */
export const backtestChartGap = createChartGapStore('ab-backtest-chart-right-gap')
